import React, { useEffect, useRef, useState } from "react";
import {
  Activity,
  FileSpreadsheet,
  Search,
  X,
  Calendar,
  TrendingUp,
  CheckCircle,
  ScanBarcode,
  PackagePlus,
} from "lucide-react";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import generatePDF from "./DocumentPdfTemplates";
import DRAFTIMAGE from "../../utils/draft.png";
import APPROVEDIMAGE from "../../utils/approved.png";

const DocumentHeader = ({
  title,
  showSidebar,
  setShowSidebar,
  searchTerm,
  setSearchTerm,
  showType,
  setShowType = () => { },
  onExport,
  total,
  documentType,
  selectedRowData,
  config,
  todayQuotationsCount = 0,
  monthQuotationsCount = 0,
  billedQuotationsCount = 0,
  doctype = "DO NO",
  showFilter = true,
  onBarcodeClick = () => { },
  onNewArrivalClick = () => { },
  showPrint = true,
  showActivitySidebar = true,
  onQuotationPrint,
  onExportItems,
}) => {
  const inputRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [loadingPrintData, setLoadingPrintData] = useState(false);
  const [error, setError] = useState(null);
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;
  const VATMODE = CURRENT_USER_OBJECT[0]?.VATMODE;

  const handleChange = (e) => {
    const value = e.target.value;
    setSelectedTemplate(value);
    if (value) {
      if (value === "quotation_print_cubix" && onQuotationPrint) {
        onQuotationPrint(selectedRowData);
      } else {
        handlePrint(value);
      }
      setSelectedTemplate("");
    }
  };

  useEffect(() => {
    const handleShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const gradientMap = {
    SALES_ORDER: "from-emerald-500 via-green-600 to-teal-700",
    DELIVERY_ORDER: "from-indigo-600 via-indigo-700 to-purple-700",
    PURCHASE_INVOICE: "from-amber-500 via-orange-600 to-red-600",
    QUOTATION: "from-purple-500 via-violet-600 to-fuchsia-700",
    SALES_RETURN: "from-red-500 via-orange-600 to-orange-600",
    PURCHASE_ORDER: "from-lime-500 via-emerald-600 to-green-700",
    SAMPLE_ISSUE: "from-sky-500 via-blue-600 to-indigo-700",
    SAMPLE_RETURN: "from-pink-500 via-rose-600 to-red-600",
    WORKORDER: "from-cyan-500 via-sky-600 to-blue-700",
    LOGISTICLIST: "from-yellow-500 via-amber-600 to-orange-600",
  };

  const gradientClass =
    gradientMap[documentType] || "from-gray-600 via-gray-700 to-gray-800";

  const prefix =
    documentType === "SALES_ORDER"
      ? "SO"
      : documentType === "QUOTATION"
        ? "QT"
        : documentType === "SALES_RETURN"
          ? "SRET"
          : "DO";

  const handlePrint = async (selectedTemplate, docNo) => {
    setLoadingPrintData(true);
    setError(null);

    const templateLabels = {
      [`${prefix.toLowerCase()}_template1`]: `${prefix} - Without Header (Plain)`,
      [`${prefix.toLowerCase()}_template2`]: `${prefix} - With Header (Pre-printed)`,
      [`${prefix.toLowerCase()}_template3`]: `${prefix} - Without Header + Code`,
      [`${prefix.toLowerCase()}_template4`]: `${prefix} - With Header + Code`,
      purchase_template1: "Purchase Invoice",
      purchase_template2: "Purchase Invoice FC",
      quotation_template1: "Quotation (With Code)",
      quotation_template2: "Quotation (Without Code)",
    };

    try {
      const apiDocType =
        documentType === "SALES_ORDER"
          ? "SORD"
          : documentType === "QUOTATION"
            ? "QUOT"
            : documentType === "SALES_RETURN"
              ? "SRET"
              : documentType === "PURCHASE_INVOICE"
                ? "PINV"
                : documentType === "PURCHASE_ORDER"
                  ? "PORD"
                  : documentType === "SAMPLE_ISSUE"
                    ? "SAMPLEISSUE"
                    : documentType === "SAMPLE_RETURN"
                      ? "SAMPLERETURN"
                      : documentType === "WORKORDER"
                        ? "WORD"
                        : documentType === "WARRANTYCLAIM"
                          ? "CLAIM"
                          : documentType === "STOCKTRANSFEROUT"
                            ? "STOCKTRANSFEROUT"
                            : documentType === "STOCKTRANSFERIN"
                              ? "STOCKTRANSFERIN"
                              : documentType === "BRANCHTRANSFER"
                                ? "BTOUT"
                                : documentType === "GOODSINTRANSIT"
                                  ? "GIT"
                                  : documentType === "LOCATIONTRANSFER"
                                    ? "LOC"
                                    : documentType === "PURCHASE_ENQUIRY"
                                      ? "PENQ"
                                      : documentType === "PURCHASERETURN"
                                        ? "PRET"
                                        : "DORD";
      const dataKey =
        documentType === "SALES_ORDER"
          ? "SO NO"
          : documentType === "QUOTATION"
            ? "QUOT NO"
            : documentType === "SALES_RETURN"
              ? "SR_NO"
              : documentType === "PURCHASE_INVOICE"
                ? "SRVNO"
                : documentType === "PURCHASE_ORDER"
                  ? "PO NO"
                  : documentType === "SAMPLE_ISSUE"
                    ? "DEMNO"
                    : documentType === "SAMPLE_RETURN"
                      ? "DEMNO"
                      : documentType === "WORKORDER"
                        ? "WO NO"
                        : documentType === "WARRANTYCLAIM"
                          ? "CLAIM_NO"
                          : documentType === "STOCKTRANSFEROUT"
                            ? "GIT NO"
                            : documentType === "STOCKTRANSFERIN"
                              ? "GIT NO"
                              : documentType === "BRANCHTRANSFER"
                                ? "TROUT_NO"
                                : documentType === "GOODSINTRANSIT"
                                  ? "GIT NO"
                                  : documentType === "LOCATIONTRANSFER"
                                    ? "TRFRNO"
                                    : documentType === "PURCHASE_ENQUIRY"
                                      ? "PO NO"
                                      : documentType === "PURCHASERETURN"
                                        ? "PR NO"
                                        : "DO NO";
      const url = `${config?.BASE_URL}/api/DocumentEditDetails/${config?.COMPANY_CODE}/${apiDocType}/${selectedRowData?.[dataKey]}/${config?.DEPARTMENT_NO}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch ${documentType} data: ${response.statusText}`,
        );
      }

      const deliveryData = await response.json();
      if (!deliveryData || deliveryData?.length === 0) {
        alert("No data available to print");
        return;
      }

      let otherCosts = [];

      if (documentType === "PURCHASE_INVOICE") {
        try {
          const otherCostUrl = `${config?.BASE_URL}/api/DocumentEditDetails/${config?.COMPANY_CODE}/PINVOC/${selectedRowData?.[dataKey]}/${config?.DEPARTMENT_NO}`;
          const otherCostResponse = await fetch(otherCostUrl);

          if (otherCostResponse.ok) {
            otherCosts = await otherCostResponse.json();
          } else {
            console.warn("Failed to fetch Other Costs");
          }
        } catch (err) {
          console.error("Error fetching Other Costs", err);
        }
      }

      let qrCodeImage = null;
      if (documentType === "SALES_RETURN" && selectedTemplate === "sret_template_saudi") {
        try {
          const srNo = selectedRowData?.["SR_NO"] || "";
          const qrUrl = `${config?.BASE_URL}/api/CRMZatcaQRGen/${config?.COMPANY_CODE}/RETURN/${srNo}/${config?.DEPARTMENT_NO}`;
          const qrResponse = await fetch(qrUrl);
          if (qrResponse.ok) {
            const qrData = await qrResponse.json();
            if (qrData && qrData.length > 0 && qrData[0].QR_BASE64) {
              try {
                const canvas = document.createElement("canvas");
                await QRCode.toCanvas(canvas, qrData[0].QR_BASE64, {
                  width: 200,
                  margin: 1,
                  errorCorrectionLevel: "M",
                });
                qrCodeImage = canvas.toDataURL("image/png");
              } catch (qrErr) {
                console.warn("Error generating QR code image:", qrErr);
              }
            }
          }
        } catch (err) {
          console.warn("Error fetching QR code for sales return:", err);
        }
      }

      const doc = new jsPDF({
        compress: true
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const templateId = selectedTemplate;
      const printType =
        documentType === "SALES_ORDER"
          ? "sales_order"
          : documentType === "QUOTATION"
            ? "quotation"
            : documentType === "SALES_RETURN"
              ? "sales_return"
              : documentType === "PURCHASE_INVOICE"
                ? "purchase_invoice"
                : documentType === "PURCHASE_ORDER"
                  ? "purchase_order"
                  : documentType === "PURCHASE_ENQUIRY"
                    ? "purchase_enquiry"
                    : documentType === "PURCHASERETURN"
                      ? "purchase_return"
                      : documentType === "BRANCHTRANSFER"
                        ? "branch_transfer_out"
                        : (documentType === "SAMPLEISSUE" || documentType === "SAMPLE_ISSUE")
                          ? "sample_issue"
                          : (documentType === "SAMPLERETURN" || documentType === "SAMPLE_RETURN")
                            ? "sample_return"
                            : documentType === "WORKORDER"
                              ? "work_order"
                              : documentType === "WARRANTYCLAIM"
                                ? "warranty_claim"
                                : documentType === "STOCKTRANSFEROUT"
                                  ? "stock_transfer_out"
                                  : documentType === "STOCKTRANSFERIN"
                                    ? "stock_transfer_in"
                                    : documentType === "BRANCHTRANSFER"
                                      ? "branch_transfer_out"
                                      : documentType === "GOODSINTRANSIT"
                                        ? "goodsin_transit"
                                        : documentType === "LOCATIONTRANSFER"
                                          ? "location_transfer"
                                          : documentType === "PURCHASE_ENQUIRY"
                                            ? "purchase_enquiry"
                                            : documentType === "PURCHASERETURN"
                                              ? "purchase_return"
                                              : "delivery_order";

      let reportSealImage = null;
      let reportTranshipSignature = null;
      let statusStamp = null;
      const requiresStatusCheck = [
        "SALES_ORDER",
        "PURCHASE_ORDER",
        // "WORKORDER",
      ].includes(documentType);
      const shouldFetchSeal = requiresStatusCheck
        ? deliveryData[0]?.status1 === "Y"
        : true;

      if (shouldFetchSeal) {
        try {
          const sealUrl = `${config?.BASE_URL}/api/CpaysCount/${config?.COMPANY_CODE}/DEPTLH/${config?.DEPARTMENT_NO}/-`;
          const sealResponse = await fetch(sealUrl);
          if (sealResponse.ok) {
            const sealData = await sealResponse.json();
            if (sealData && sealData.length > 0) {
              reportSealImage = sealData[0]?.SEAL;
            }
          }
        } catch (err) {
          console.error("Error fetching seal image", err);
        }

        if (
          documentType === "PURCHASE_ORDER" &&
          deliveryData[0]?.status1 === "Y" &&
          deliveryData[0]?.tranship
        ) {
          try {
            const transhipUrl = `${config?.BASE_URL}/api/CpaysCount/${config?.COMPANY_CODE}/BASE64PICK/${deliveryData[0]?.tranship}/signature`;
            const transhipResponse = await fetch(transhipUrl);
            if (transhipResponse.ok) {
              const transhipData = await transhipResponse.json();
              if (transhipData && transhipData.length > 0) {
                reportTranshipSignature = transhipData[0]?.imgbase64;
              }
            }
          } catch (err) {
            console.error("Error fetching tranship signature", err);
          }
        }
      }
      if (documentType === "PURCHASE_ORDER") {
        statusStamp =
          deliveryData[0]?.status1 === "Y" ? APPROVEDIMAGE : DRAFTIMAGE;
      }

      generatePDF(
        doc,
        pageWidth,
        pageHeight,
        templateId,
        deliveryData,
        config.COMPANY_NAME,
        printType,
        config.REPORT_HEADER_IMAGE,
        config.REPORT_FOOTER_IMAGE,
        otherCosts,
        VATPERCENTAGE,
        VATMODE,
        reportSealImage,
        reportTranshipSignature,
        statusStamp,
        qrCodeImage,
      );
      const pdfBlob = doc.output("bloburl");
      window.open(pdfBlob);
      const payloadDocNo =
        documentType === "SALES_ORDER"
          ? "so_no"
          : documentType === "QUOTATION"
            ? "so_no"
            : documentType === "SALES_RETURN"
              ? "sr_no"
              : documentType === "PURCHASE_INVOICE"
                ? "inv_no"
                : documentType === "PURCHASE_ORDER"
                  ? "po_no"
                  : documentType === "SAMPLE_ISSUE"
                    ? "DEMNO"
                    : documentType === "SAMPLE_RETURN"
                      ? "DEMNO"
                      : documentType === "WORKORDER"
                        ? "WO NO"
                        : documentType === "WARRANTYCLAIM"
                          ? "CLAIM_NO"
                          : documentType === "GOODSINTRANSIT"
                            ? "GIT NO"
                            : documentType === "LOCATIONTRANSFER"
                              ? "TRFRNO"
                              : documentType === "BRANCHTRANSFER"
                                ? "inv_no"
                                : documentType === "QUOTATION"
                                  ? "so_no"
                                  : "do_no";
      const payloadDocType =
        documentType === "SALES_ORDER"
          ? "SOPRINT"
          : documentType === "QUOTATION"
            ? "QOPRINT"
            : documentType === "SALES_RETURN"
              ? "SALESRETURNPRINT"
              : documentType === "PURCHASE_INVOICE"
                ? "PINVPRINT"
                : documentType === "PURCHASE_ORDER"
                  ? "POPRINT"
                  : documentType === "SAMPLE_ISSUE"
                    ? "SAMPLEISSUEPRINT"
                    : documentType === "SAMPLE_RETURN"
                      ? "SAMPLE_RETURNPRINT"
                      : documentType === "WORKORDER"
                        ? "WOPRINT"
                        : documentType === "WARRANTYCLAIM"
                          ? "CLAIMPRINT"
                          : documentType === "GOODSINTRANSIT"
                            ? "GITPRINT"
                            : documentType === "LOCATIONTRANSFER"
                              ? "TRFRPRINT"
                              : documentType === "BRANCHTRANSFER"
                                ? "BTPRINT"
                                // : documentType === "QUOTATION"
                                //   ? "QTPRINT"
                                : "DELIVERYPRINT";
      const logPayload = [
        {
          cmpcode: config.COMPANY_CODE,
          doc_no: deliveryData[0]?.[payloadDocNo] || "",
          status: "printed",
          evaluationcomments: `Printed ${templateLabels[selectedTemplate] || selectedTemplate
            }`,
          userid: config.CURRENT_USER,
          deptno: config.DEPARTMENT_NO,
          doctype: payloadDocType,
        },
      ];

      await fetch(`${config.BASE_URL}/api/CRMDocLogReg`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(logPayload),
      });
    } catch (err) {
      setError(err.message);
      console.error("Error printing delivery note:", err);
    } finally {
      setLoadingPrintData(false);
    }
  };

  return (
    <div className={` bg-gradient-to-r ${gradientClass} shadow-lg rounded-lg`}>
      <div className="px-4 py-4">
        <div
          className={`flex lg:flex-row flex-col items-center justify-between gap-4 ${documentType === "QUOTATION" ? "mb-4" : ""
            }`}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-white tracking-tight">
                {title}
              </h1>
              <div className="px-2.5 py-0.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-bold border border-white/30">
                {total || 0}
              </div>
            </div>

            {showActivitySidebar && (
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${showSidebar
                  ? "bg-white text-indigo-700 shadow-md"
                  : "bg-white/10 text-white hover:bg-white/20 border border-white/30"
                  }`}
              >
                <Activity className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">
                  {showSidebar ? "Hide" : "Show"} Activity
                </span>
              </button>
            )}
          </div>

          <div className="relative flex-1 max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white z-10 pointer-events-none" />

            <input
              ref={inputRef}
              type="text"
              placeholder={`Search ${doctype}, customer, date...`}
              value={searchTerm}
              autoFocus
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-20 py-2.5 text-sm rounded-lg outline-none
                bg-white/20 text-white placeholder:text-white/70
                backdrop-blur-sm border border-white/30
                focus:bg-white/30 focus:border-white/50 focus:ring-2 focus:ring-white/40
                hover:bg-white/25 transition-all shadow-sm hover:shadow-md"
            />

            {!searchTerm && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] lg:flex hidden text-white/60 bg-white/10 border border-white/20 rounded px-2 py-0.5 font-mono">
                Ctrl + K
              </div>
            )}

            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-white/30 rounded hover:bg-white/40 transition-all shadow z-10"
              >
                <X className="w-3 h-3" />
                <span>Clear</span>
              </button>
            )}
          </div>

          {/* Only for PURCHASE_INVOICE */}
          <div className="flex items-center gap-2">
            {/* {documentType === "PURCHASE_INVOICE" && (
              <div className="flex items-center gap-2 mr-2">
                <button
                  onClick={onBarcodeClick}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-white/10 border border-white/30 rounded-lg hover:bg-white/20 transition-all shadow-sm hover:shadow-md"
                  title="Open Barcode Scanner"
                >
                  <ScanBarcode className="w-4 h-4" />
                  <span className="hidden sm:inline">Barcode</span>
                </button>

                <button
                  onClick={onNewArrivalClick}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-amber-700 bg-white rounded-lg hover:bg-white/90 transition-all shadow-md hover:shadow-lg"
                  title="Register New Arrival"
                >
                  <PackagePlus className="w-4 h-4" />
                  <span className="hidden sm:inline">New Arrival</span>
                </button>
              </div>
            )} */}

            {showFilter && (
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg p-0.5 border border-white/20">
                {["ALL", "OPEN"].map((opt) => {
                  const valueToPass =
                    documentType === "QUOTATION"
                      ? opt === "ALL"
                        ? "-"
                        : "OPEN"
                      : opt;

                  const isActive = showType === valueToPass;

                  return (
                    <button
                      key={opt}
                      onClick={() => setShowType(valueToPass)}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${isActive
                        ? "bg-white text-indigo-700 shadow-sm"
                        : "text-white/90 hover:text-white hover:bg-white/10"
                        }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}

            {showPrint && (
              <div className="w-40">
                <select
                  value={selectedTemplate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-xs border bg-white border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="" disabled>
                    Select Print Option
                  </option>

                  {documentType === "SALES_RETURN" ? (
                    <>
                      <option value="sret_template1">Sales Return Note</option>
                      {(VATMODE === "SAUDIPHASE1" || VATMODE === "SAUDIPHASE2") && (
                        <option value="sret_template_saudi">Sales Return - Saudi</option>
                      )}
                    </>
                  ) : documentType === "PURCHASE_INVOICE" ? (
                    <>
                      <option value="purchase_template1">
                        Purchase Invoice
                      </option>
                      <option value="purchase_template2">
                        Purchase Invoice FC
                      </option>
                    </>
                  ) : documentType === "SAMPLE_ISSUE" ? (
                    <option value="sample_issue_template1">
                      Sample Issue Note
                    </option>
                  ) : documentType === "SAMPLE_RETURN" ? (
                    <option value="sample_return_template1">
                      Sample Return Note
                    </option>
                  ) : documentType === "WORKORDER" ? (
                    <option value="work_order_template1">Work Order</option>
                  ) : documentType === "WARRANTYCLAIM" ? (
                    <option value="warranty_claim_template1">
                      Warranty Claim
                    </option>
                  ) : documentType === "GOODSINTRANSIT" ? (
                    <option value="goods_in_transit_template1">
                      Goods In Transit
                    </option>
                  ) : documentType === "STOCKTRANSFEROUT" ? (
                    <option value="stock_transfer_out_template1">
                      Stock Transfer Out
                    </option>
                  ) : documentType === "STOCKTRANSFERIN" ? (
                    <option value="stock_transfer_in_template1">
                      Stock Transfer In
                    </option>
                  ) : documentType === "BRANCHTRANSFER" ? (
                    <option value="branch_transfer_out_template1">
                      Branch Transfer 1
                    </option>
                  ) : documentType === "PURCHASE_ORDER" ? (
                    <option value="po_template1">Purchase Order</option>)
                    : documentType === "PURCHASE_ENQUIRY" ? (
                      <option value="enq_template1">Purchase Enquiry</option>
                    ) : documentType === "PURCHASERETURN" ? (
                      <option value="pr_template1">Purchase Return</option>
                    ) : documentType === "LOCATIONTRANSFER" ? (
                      <option value="location_transfer_template1">Location Transfer</option>
                    ) : documentType === "QUOTATION" ? (
                      <option value="quotation_print_cubix">
                        Print Quotation
                      </option>
                    ) : (
                      <>
                        <option value={`${prefix.toLowerCase()}_template1`}>
                          {prefix} - Without Header (Plain)
                        </option>
                        <option value={`${prefix.toLowerCase()}_template2`}>
                          {prefix} - With Header (Pre-printed)
                        </option>
                        <option value={`${prefix.toLowerCase()}_template3`}>
                          {prefix} - Without Header + Code
                        </option>
                        <option value={`${prefix.toLowerCase()}_template4`}>
                          {prefix} - With Header + Code
                        </option>

                        {documentType === "SALES_ORDER" && (
                          <option value={`${prefix.toLowerCase()}_template5`}>
                            {prefix} - No Balance
                          </option>
                        )}
                      </>
                    )}
                </select>
              </div>
            )}

            <button
              onClick={onExport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-all shadow-md hover:shadow-lg"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Export List</span>
            </button>

            {onExportItems && (
              <button
                onClick={onExportItems}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-all shadow-md hover:shadow-lg"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Export Items</span>
              </button>
            )}
          </div>
        </div>

        {/* Only visible for QUOTATION documentType */}
        {documentType === "QUOTATION" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {/* Today's Quotations */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg p-3 hover:bg-white/20 transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium">
                      Today's Quotations
                    </p>
                    <p className="text-2xl font-bold text-white">
                      {todayQuotationsCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Month's Quotations */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg p-3 hover:bg-white/20 transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium">
                      This Month
                    </p>
                    <p className="text-2xl font-bold text-white">
                      {monthQuotationsCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Billed Quotations */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg p-3 hover:bg-white/20 transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-medium">
                      Billed This Month
                    </p>
                    <p className="text-2xl font-bold text-white">
                      {billedQuotationsCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentHeader;
